// The timeline scrubber — a bottom-centre HUD transport bar.
//
// Shape follows `process/StepScrubber` (the board's established scrubber
// idiom) and DESIGN-SYSTEM.md: HudChip vocabulary, the 12px-inset corner grid,
// bottom-centre placement already reserved for a scrubber (§2).
//
// It renders ONLY when the model carries a lifecycle decoration, so every
// existing model — which carries none — sees no new chrome at all. Because
// board chrome is absolutely positioned, appearing overlays rather than
// reflowing.
//
// The axis is DISCRETE: one stop per marker the model writes, plus `now`. It
// steps rather than slides, because the markers are an ordered list and nothing
// claims to know the distance between `v1` and `v2`.

import { useEffect, useMemo } from "react";
import { cx, FOCUS_RING } from "@archlang/ui";
import { useTranslation } from "../../i18n";
import type { LifecycleWindow, Timeline } from "@archlang/engine";
import type { ScrubSelection } from "./lifecycleScrubStore.js";
import {
  resetScrub,
  setComparePoint,
  setScrubPoint,
  useComparePoint,
  useScrubPoint,
} from "./lifecycleScrubStore.js";
import { scrubStops, stopIndexOf, type ScrubStop } from "./timeline.js";

export interface LifecycleScrubberProps {
  /** Scrub-independent windows from the projection plan. Empty ⇒ no scrubber. */
  readonly windows: ReadonlyMap<string, LifecycleWindow>;
  /** The model's derived marker axis. */
  readonly timeline?: Timeline;
  /** Phone treatment: full-width glass bar in thumb territory. */
  readonly mobile?: boolean;
}

export function LifecycleScrubber({ windows, timeline, mobile }: LifecycleScrubberProps) {
  const { t } = useTranslation("lifecycle");
  const at = useScrubPoint();
  const compare = useComparePoint();
  const stops = useMemo(() => scrubStops(timeline), [timeline]);

  // An edit can delete the marker the handle is parked on. Release the point
  // rather than only DRAWING the parked detent: leaving it stored painted the
  // board at the vanished marker's virtual ordinal while the handle and label
  // both read "All" — the transport claiming one thing and the board showing
  // another. Effect, not render, because this mutates a store.
  const stale = at !== null && stops.length > 0 && stopIndexOf(stops, at) === -1;
  useEffect(() => {
    if (stale) resetScrub();
  }, [stale]);

  // No lifecycle anywhere in the model — or nothing dated to scrub along — ⇒ no
  // scrubber. This is the whole reason existing boards are untouched.
  if (windows.size === 0 || stops.length === 0) return null;

  const last = stops.length - 1;
  // The parked "All" detent sits one step PAST the end of the axis, so it is a
  // labelled state rather than a coordinate — "showing everything" can never be
  // confused with "scrubbed late enough to include everything".
  const parked = at === null;
  // A point naming a marker the axis no longer has falls back to the detent
  // rather than snapping to some other stop and lying about where the board is.
  const found = stopIndexOf(stops, at);
  const value = parked || found === -1 ? stops.length : found;

  const labelFor = (stop: ScrubStop | undefined): string => stop?.marker ?? t("scrubber.now");
  const labelOf = (point: ScrubSelection): string =>
    point === null || point.kind === "now" ? t("scrubber.now") : point.text;

  const onInput = (raw: number): void => {
    if (raw > last) resetScrub();
    else setScrubPoint(stops[raw]!.point);
  };

  return (
    <div
      className={
        mobile
          ? "pointer-events-auto fixed inset-x-2 bottom-2 z-30 flex flex-col gap-1 rounded-2xl border border-slate-700 bg-slate-900/(--tint-solid) px-3 py-2 backdrop-blur-md"
          : "pointer-events-auto absolute bottom-3 left-1/2 z-30 flex -translate-x-1/2 flex-col gap-1 rounded-full border border-slate-700 bg-slate-900/(--tint-strong) px-3 py-1.5 shadow-lg backdrop-blur-md"
      }
      data-testid="lifecycle-scrubber"
    >
      <div className="flex items-center gap-2 text-xs whitespace-nowrap">
        <span className="text-slate-400">{t("scrubber.label")}</span>
        <input
          type="range"
          min={0}
          max={stops.length}
          value={value}
          onChange={(e) => onInput(Number(e.currentTarget.value))}
          className="h-1 w-56 cursor-pointer appearance-none rounded-full bg-slate-700 accent-arch-400"
          aria-label={t("scrubber.rangeAria")}
          data-testid="lifecycle-scrub-range"
        />
        <span
          className={parked ? "text-arch-200" : "text-slate-200"}
          data-testid="lifecycle-scrub-label"
        >
          {value === stops.length ? t("scrubber.all") : labelFor(stops[value])}
        </span>
        <button
          type="button"
          onClick={() => (compare === null ? setComparePoint(stops[last]!.point) : setComparePoint(null))}
          className={cx(
            FOCUS_RING,
            compare === null
              ? "rounded-full border border-slate-700 px-2 py-0.5 text-slate-300 hover:text-white"
              : "rounded-full border border-arch-400 bg-arch-500/(--tint-faint) px-2 py-0.5 text-arch-200",
          )}
          data-testid="lifecycle-compare-toggle"
        >
          {t("scrubber.compare")}
        </button>
        {value !== stops.length && (
          <button
            type="button"
            onClick={resetScrub}
            className={cx("rounded-full border border-slate-700 px-2 py-0.5 text-slate-300 hover:text-white", FOCUS_RING)}
            data-testid="lifecycle-scrub-reset"
          >
            {t("scrubber.all")}
          </button>
        )}
      </div>
      {/* Marker ticks — evenly spaced, labelled with the author's own token.
          The axis is an ORDER, not a duration: `v1`→`v2` and `v2`→`v3` are one
          step each because nothing in the model says otherwise. */}
      <div className="relative h-3 w-56 self-center">
        {stops.map((stop) => (
          <span
            key={stop.key}
            className={cx(
              "absolute -translate-x-1/2 text-[9px]",
              stop.marker ? "text-slate-500" : "text-arch-300",
            )}
            style={{ left: `${stop.frac * 100}%` }}
          >
            {labelFor(stop)}
          </span>
        ))}
      </div>
      {compare !== null && (
        <div className="self-center text-[10px] text-arch-200" data-testid="lifecycle-compare-label">
          {labelOf(at)} → {labelOf(compare)}
        </div>
      )}
    </div>
  );
}
